import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CalendarOff, CalendarRange, ClipboardCheck, Loader2, Settings2, SprayCan, Sparkles, UserRound, Wheat } from 'lucide-react';
import { pulizieApi, erroreApi, dataBreve } from './pulizie/api';
import { ZonaCard } from './pulizie/ZonaCard';
import { ProblemaDialog } from './pulizie/ProblemaDialog';
import { AllergeniDialog } from './pulizie/AllergeniDialog';
import { RegistroDialog } from './pulizie/RegistroDialog';
import { PianoDialog } from './pulizie/PianoDialog';
import { ProdottiDialog } from './pulizie/ProdottiDialog';
import { OperatoreDialog, useOperatore } from './pulizie/OperatoreDialog';
import { CompilaPeriodoDialog } from './pulizie/CompilaPeriodoDialog';

const AvvisoSaltate = ({ saltate }) => (
  <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="pulizie-avviso-saltate">
    <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
      <AlertTriangle className="h-4 w-4" />Pulizie non registrate
    </p>
    {saltate.map((s) => (
      <p key={s.data} className="mt-1 text-sm text-amber-900"><span className="capitalize">{dataBreve(s.data)}</span>: {s.zone.join(', ')}</p>
    ))}
  </div>
);

const Azione = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-white border border-gray-200 p-3 text-xs font-medium text-gray-700 hover:border-gray-400 transition-colors">
    <Icon className="h-5 w-5" />{label}
  </button>
);

const Progresso = ({ dati, operatore, onCambiaOperatore }) => {
  const tot = dati.da_fare + dati.fatte;
  const perc = tot ? Math.round((dati.fatte / tot) * 100) : 100;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="pulizie-progresso">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs text-gray-500 capitalize">{new Date(`${dati.data}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <p className="text-2xl font-bold text-gray-900">{dati.fatte} di {tot} zone fatte</p>
        </div>
        <span className="text-sm font-semibold text-emerald-600">{perc}%</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${perc}%` }} />
      </div>
      <button onClick={onCambiaOperatore} data-testid="pulizie-operatore-btn"
        className="mt-3 w-full flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-left hover:border-gray-400 transition-colors">
        <UserRound className="h-4 w-4 text-gray-500" />
        <span className="flex-1 text-sm text-gray-700">Chi pulisce: <b className="text-gray-900" data-testid="pulizie-operatore-nome">{operatore}</b></span>
        <span className="text-xs font-semibold text-emerald-600">Cambia</span>
      </button>
    </div>
  );
};

const PuliziaSection = () => {
  const [dati, setDati] = useState(null);
  const [finestra, setFinestra] = useState(null);
  const [zonaProblema, setZonaProblema] = useState(null);
  const [operatore, setOperatore] = useOperatore(dati?.utente);

  const carica = useCallback(async () => {
    try { setDati(await pulizieApi.get('/oggi')); } catch (e) { toast.error(erroreApi(e, 'Pulizie non disponibili')); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const annulla = async (r) => {
    try { await pulizieApi.del(`/registro/${r.id}`); toast.success('Registrazione annullata'); carica(); } catch (e) { toast.error(erroreApi(e, 'Non annullata')); }
  };
  const fatto = async (zona) => {
    try {
      const { registrazione: r } = await pulizieApi.post('/fatto', { zona, operatore });
      toast.success(`${zona} registrata alle ${r.ora}`, { action: { label: 'Annulla', onClick: () => annulla(r) } });
      carica();
    } catch (e) { toast.error(erroreApi(e, 'Non registrata')); }
  };

  const compilato = (r) => {
    setFinestra(null);
    const annullaLotto = async () => {
      try { await pulizieApi.del(`/compila-periodo/${r.lotto}`); toast.success('Compilazione annullata'); carica(); } catch (e) { toast.error(erroreApi(e, 'Non annullata')); }
    };
    toast.success(`Inserite ${r.inserite} pulizie`, { duration: 10000, action: { label: 'Annulla', onClick: annullaLotto } });
    carica();
  };

  if (!dati || !operatore) return <div className="flex justify-center p-10"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="pulizie-section">
      <Progresso dati={dati} operatore={operatore} onCambiaOperatore={() => setFinestra('operatore')} />
      {dati.saltate.length > 0 && <AvvisoSaltate saltate={dati.saltate} />}
      {!dati.aperto && (
        <p className="flex items-center gap-2 text-sm text-gray-600" data-testid="pulizie-chiuso">
          <CalendarOff className="h-4 w-4" />Oggi è giorno di chiusura: restano solo le pulizie periodiche.
        </p>
      )}
      <div className={`grid gap-2 ${dati.utente.responsabile ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-4'}`}>
        <Azione icon={Wheat} label="Allergeni" onClick={() => setFinestra('allergeni')} testid="pulizie-btn-allergeni" />
        <Azione icon={ClipboardCheck} label="Registro" onClick={() => setFinestra('registro')} testid="pulizie-btn-registro" />
        <Azione icon={Settings2} label="Piano" onClick={() => setFinestra('piano')} testid="pulizie-btn-piano" />
        <Azione icon={SprayCan} label="Detergenti" onClick={() => setFinestra('prodotti')} testid="pulizie-btn-prodotti" />
        {dati.utente.responsabile && (
          <Azione icon={CalendarRange} label="Compila periodo" onClick={() => setFinestra('compila')} testid="pulizie-btn-compila" />
        )}
      </div>
      {dati.allergeni_oggi.length > 0 && (
        <p className="text-sm text-violet-300 lg:text-violet-700" data-testid="pulizie-allergeni-oggi">
          Pulizia dopo allergeni oggi: {dati.allergeni_oggi.map((r) => `${r.ora} (${r.operatore})`).join(', ')}
        </p>
      )}
      <div className="space-y-3">
        {dati.zone.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center" data-testid="pulizie-vuoto">
            <Sparkles className="h-8 w-8 mx-auto text-emerald-500" />
            <p className="mt-2 font-semibold text-gray-900">Niente da pulire oggi</p>
          </div>
        ) : dati.zone.map((z) => <ZonaCard key={z.zona} zona={z} onFatto={fatto} onProblema={setZonaProblema} onAnnulla={annulla} />)}
      </div>

      <ProblemaDialog zona={zonaProblema} operatore={operatore} onClose={() => setZonaProblema(null)} onSaved={() => { setZonaProblema(null); carica(); }} />
      <AllergeniDialog open={finestra === 'allergeni'} allergeni={dati.allergeni} operatore={operatore} onClose={() => setFinestra(null)}
        onSaved={() => { setFinestra(null); carica(); }} />
      <RegistroDialog open={finestra === 'registro'} onClose={() => setFinestra(null)} />
      <PianoDialog open={finestra === 'piano'} onClose={() => setFinestra(null)} onCambiato={carica} />
      <ProdottiDialog open={finestra === 'prodotti'} onClose={() => setFinestra(null)} />
      <OperatoreDialog open={finestra === 'operatore'} attuale={operatore} onClose={() => setFinestra(null)} onScegli={setOperatore} />
      {dati.utente.responsabile && (
        <CompilaPeriodoDialog open={finestra === 'compila'} operatore={operatore} zone={dati.zone_piano}
          onClose={() => setFinestra(null)} onFatto={compilato} />
      )}
    </div>
  );
};

export default PuliziaSection;
